import { Check, Store, Truck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/utils/cn';

import type { OfflineFulfillmentType } from '../types';

const OPTIONS = [
  {
    value: 'IN_STORE' as const,
    icon: Store,
    titleKey: 'fulfillment.inStore' as const,
    hintKey: 'fulfillment.inStoreHint' as const,
  },
  {
    value: 'HOME_DELIVERY' as const,
    icon: Truck,
    titleKey: 'fulfillment.homeDelivery' as const,
    hintKey: 'fulfillment.homeDeliveryHint' as const,
  },
];

export interface OfflineFulfillmentPickerProps {
  value: OfflineFulfillmentType;
  onChange: (value: OfflineFulfillmentType) => void;
  disabled?: boolean;
}

export function OfflineFulfillmentPicker({
  value,
  onChange,
  disabled = false,
}: OfflineFulfillmentPickerProps) {
  const { t } = useTranslation('offlineOrders');

  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
        {t('fulfillment.title')}
      </h2>
      <div className="grid gap-2" role="group" aria-label={t('fulfillment.title')}>
        {OPTIONS.map((option) => {
          const selected = value === option.value;
          const Icon = option.icon;
          return (
            <button
              key={option.value}
              type="button"
              disabled={disabled}
              aria-pressed={selected}
              onClick={() => onChange(option.value)}
              className={cn(
                'flex items-start gap-3 rounded-md border px-3 py-2.5 text-left transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
                'disabled:cursor-not-allowed disabled:opacity-60',
                selected
                  ? 'border-primary bg-primary-subtle'
                  : 'border-border bg-surface hover:bg-surface-muted',
              )}
            >
              <span
                className={cn(
                  'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                  selected ? 'bg-primary text-fg-on-primary' : 'bg-surface-muted text-fg-muted',
                )}
              >
                <Icon aria-hidden="true" className="h-4 w-4" strokeWidth={2} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-fg">{t(option.titleKey)}</span>
                <span className="mt-0.5 block text-xs leading-4 text-fg-muted">
                  {t(option.hintKey)}
                </span>
              </span>
              <Check
                aria-hidden="true"
                className={cn(
                  'mt-1 h-4 w-4 shrink-0',
                  selected ? 'text-primary' : 'text-transparent',
                )}
                strokeWidth={2.4}
              />
            </button>
          );
        })}
      </div>
    </section>
  );
}
